import { apiClient, ApiClient } from "@/lib/api/api-client";
import {
  BusinessAddress,
  BusinessCreateInfo,
  BusinessInfo,
  BusinessPosts,
  BusinessShortInfo,
  BusinessUpdateInfo,
  CreateBusinessAddress,
  UpdateBusinessAddress,
} from "@/modules/types/business";
import { BaseResponse } from "@/modules/types/response";
import { Subscribe } from "@/modules/types/subscription/subscription";
import { BusinessEmployee } from "@/modules/types/employee";
import { ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies";

export interface BusinessInfoResponse extends BaseResponse {
  businessInfo?: BusinessInfo;
  total?: number;
}

export interface BusinessUpdateResponse extends BaseResponse {
  businessInfo?: BusinessInfo;
}

export interface BusinessPostsResponse extends BaseResponse {
  businessPosts: BusinessPosts;
}

export interface BusinessSubscribeResponse extends BaseResponse {
  subscribe: Subscribe;
  businessPosts: BusinessPosts;
}

export interface CreateBusinessAddressResponse extends BaseResponse {
  address: BusinessAddress;
}

export interface ManagedBusinessesResponse extends BaseResponse {
  businessInfo: BusinessShortInfo[];
}

export interface BusinessEmployeesResponse extends BaseResponse {
  employees: BusinessEmployee[];
  total: number;
  page: number;
  limit: number;
}

export interface InviteEmployeesResponse extends BaseResponse {
  invitedEmails?: string[];
}

export class BusinessService {
  private BASE_PATH =
    process.env.API_BUSINESS_ENDPOINT || "http://localhost:8003/business";
  private apiClient: ApiClient = apiClient;
  private apiVersion = "v1.0.0";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  /**
   * This is method to get the business information
   * @param businessId (optional) - if not provided, it will get first business in the list
   * @returns returns the business information
   */
  async getBusinessInfo(
    businessId?: string,
    cookies?: (() => Promise<ReadonlyRequestCookies>) | undefined,
  ): Promise<BusinessInfoResponse> {
    if (cookies) this.apiClient.setCookies(cookies);
    const params = new URLSearchParams();
    if (businessId) params.set("id", businessId);
    return await this.apiClient.fetch<BusinessInfoResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/information?${params.toString()}`,
    );
  }

  async getPostsBusiness(businessId: string): Promise<BusinessPostsResponse> {
    return await this.apiClient.fetch<BusinessPostsResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/subscribed/posts?businessId=${businessId}`,
    );
  }

  async subscribePack(
    businessId: string,
    subscriptionId: string,
  ): Promise<BusinessSubscribeResponse> {
    return await this.apiClient.fetch<BusinessSubscribeResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/subscribe`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({ businessId, subscriptionId }),
      },
    );
  }

  async subscribeFreeTrial(
    businessId: string,
  ): Promise<BusinessSubscribeResponse> {
    return await this.apiClient.fetch<BusinessSubscribeResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/subscribe/free-trial`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({ businessId }),
      },
    );
  }

  async updateBusinessInformation(
    data: BusinessUpdateInfo,
  ): Promise<BusinessUpdateResponse> {
    data.industry = undefined;
    return await this.apiClient.fetch<BusinessUpdateResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/information`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  }

  async createBusinessAddress(
    address: CreateBusinessAddress,
  ): Promise<CreateBusinessAddressResponse> {
    return await this.apiClient.fetch<CreateBusinessAddressResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/address`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(address),
      },
    );
  }

  async updateBusinessAddress(
    addressId: string,
    address: UpdateBusinessAddress,
  ): Promise<boolean> {
    return await this.apiClient.fetch<boolean>(
      `${this.BASE_PATH}/${this.apiVersion}/address/${addressId}`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify(address),
      },
    );
  }

  async deleteBusinessAddress(addressId: string[]): Promise<boolean> {
    const queryParam = new URLSearchParams();
    addressId.forEach((id) => queryParam.append("ids", id));
    return await this.apiClient.fetch<boolean>(
      `${this.BASE_PATH}/${this.apiVersion}/address?${queryParam.toString()}`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "DELETE",
      },
    );
  }

  async registBusiness(dataCreate: BusinessCreateInfo): Promise<BusinessInfo> {
    const response = await this.apiClient.fetch<BusinessInfo>(
      `${this.BASE_PATH}/${this.apiVersion}/regist`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(dataCreate),
      },
    );
    return response;
  }

  async updateCompanyImages(
    businessId: string,
    images: string[],
    removedImages: string[],
  ): Promise<boolean> {
    return await this.apiClient.fetch<boolean>(
      `${this.BASE_PATH}/${this.apiVersion}/images`,
      {
        method: "PUT",
        body: JSON.stringify({
          businessId,
          newUrls: images,
          removedUrls: removedImages,
        }),
      },
    );
  }

  async getManagedBusinesses(): Promise<ManagedBusinessesResponse> {
    return await this.apiClient.fetch<ManagedBusinessesResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/employee/work-businesses`,
    );
  }

  async inviteEmployees(
    businessId: string,
    emails: string[],
  ): Promise<InviteEmployeesResponse> {
    return await this.apiClient.fetch<InviteEmployeesResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/invite`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ businessId, emails }),
      },
    );
  }

  async deleteEmployee(
    employeeId: string,
    businessId: string,
  ): Promise<BaseResponse> {
    return await this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/employee/${employeeId}?businessId=${businessId}`,
      {
        method: "DELETE",
      },
    );
  }

  async bulkDeleteEmployees(
    businessId: string,
    employeeIds: string[],
  ): Promise<BaseResponse> {
    return await this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/bulk-delete-employee`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ businessId, employeeIds }),
      },
    );
  }
}
